import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import ChartWidget from '../../components/charts/ChartWidget';
import InsightBlockWidget from '../../components/insights/InsightBlockWidget';
import ImageBlockWidget from '../../components/images/ImageBlockWidget';
import TextBlockWidget from '../../components/text/TextBlockWidget';
import UnifiedChartModal from '../../components/modals/UnifiedChartModal';
import AddInsightBlockModal from '../../components/modals/AddInsightBlockModal';
import EditInsightBlockModal from '../../components/modals/EditInsightBlockModal';
import AddImageBlockModal from '../../components/modals/AddImageBlockModal';
import AddTextBlockModal from '../../components/modals/AddTextBlockModal';
import EditTextBlockModal from '../../components/modals/EditTextBlockModal';
import ShareDashboardModal from '../../components/modals/ShareDashboardModal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowLeftIcon,
  ShareIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  LightBulbIcon,
  PhotoIcon,
  Squares2X2Icon,
  XMarkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function DashboardBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    currentDashboard, 
    charts,
    insightBlocks,
    imageBlocks,
    textBlocks,
    layout, 
    isLoading, 
    fetchDashboard, 
    updateLayout,
    updateDashboard,
    updateInsightBlockLocal,
    removeInsightBlockLocal,
    removeImageBlockLocal,
    updateTextBlockLocal,
    removeTextBlockLocal,
    resetCurrent 
  } = useDashboardStore();
  const { hasPermission } = useAuthStore();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showUnifiedModal, setShowUnifiedModal] = useState(false);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [editingInsight, setEditingInsight] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showElementPicker, setShowElementPicker] = useState(false);
  const [showTextBlockModal, setShowTextBlockModal] = useState(false);
  const [editingTextBlock, setEditingTextBlock] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [chartRefreshKey, setChartRefreshKey] = useState(0);
  const isMountedRef = useRef(true);
  
  // Permission checks
  const canEditDashboard = hasPermission('edit_dashboards');
  const canCreateCharts = hasPermission('create_dashboards');
  const canUseAI = hasPermission('use_ai');
  const canShareDashboards = hasPermission('share_dashboards');
  
  // Build a lookup map for insight blocks by ID
  const insightBlockMap = useMemo(() => {
    const map = {};
    (insightBlocks || []).forEach(ib => { map[ib._id] = ib; });
    return map;
  }, [insightBlocks]);
  
  // Build a lookup map for image blocks by ID
  const imageBlockMap = useMemo(() => {
    const map = {};
    (imageBlocks || []).forEach(ib => { map[ib._id] = ib; });
    return map;
  }, [imageBlocks]);
  
  // Build a lookup map for text blocks by ID
  const textBlockMap = useMemo(() => {
    const map = {};
    (textBlocks || []).forEach(tb => { map[tb._id] = tb; });
    return map;
  }, [textBlocks]);
  
  // Stable fetch function that handles errors and retries
  const loadDashboard = useCallback(async (dashboardId, isRetry = false) => {
    if (!dashboardId || dashboardId === 'new') return;
    
    try {
      await fetchDashboard(dashboardId);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      // Retry once on failure
      if (!isRetry && isMountedRef.current) {
        console.log('[DashboardBuilder] Retrying dashboard fetch...');
        setTimeout(() => {
          if (isMountedRef.current) {
            loadDashboard(dashboardId, true);
          }
        }, 1000);
      } else if (isMountedRef.current) {
        toast.error('Failed to load dashboard. Please try refreshing.');
      }
    }
  }, [fetchDashboard]);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    if (id && id !== 'new') {
      loadDashboard(id);
    }
    
    return () => {
      isMountedRef.current = false;
      resetCurrent();
    };
  }, [id, loadDashboard, resetCurrent]);
  
  // Manual refresh handler - also triggers all charts to refresh their data
  const handleRefreshDashboard = useCallback(async () => {
    if (!id || id === 'new' || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await fetchDashboard(id);
      // Increment refresh key to trigger all ChartWidgets to refetch their data
      setChartRefreshKey(prev => prev + 1);
      toast.success('Dashboard refreshed');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [id, fetchDashboard, isRefreshing]);
  
  // Track current responsive breakpoint (ref for layout guard, state for UI re-renders)
  const currentBreakpointRef = useRef('lg');
  const [isDesktop, setIsDesktop] = useState(true);
  
  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    if (currentBreakpointRef.current !== 'lg') return;
    
    // Build a lookup from the original layout to preserve itemType and associated IDs
    const originalMap = {};
    (layout || []).forEach(item => {
      let key;
      if (item.itemType === 'insightBlock') key = item.insightBlockId?.toString();
      else if (item.itemType === 'imageBlock') key = item.imageBlockId?.toString();
      else if (item.itemType === 'textBlock') key = item.textBlockId?.toString();
      else key = item.chartId?.toString() || item.i;
      if (key) originalMap[key] = item;
    });
    
    const formattedLayout = currentLayout.map(item => {
      const orig = originalMap[item.i];
      if (orig?.itemType === 'insightBlock') {
        return {
          itemType: 'insightBlock',
          insightBlockId: orig.insightBlockId,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        };
      }
      if (orig?.itemType === 'imageBlock') {
        return {
          itemType: 'imageBlock',
          imageBlockId: orig.imageBlockId,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        };
      }
      if (orig?.itemType === 'textBlock') {
        return {
          itemType: 'textBlock',
          textBlockId: orig.textBlockId,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        };
      }
      return {
        itemType: 'chart',
        chartId: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      };
    });
    updateLayout(formattedLayout);
  }, [updateLayout, layout]);
  
  const handleNameChange = async (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      const newName = e.target.value.trim();
      if (newName && newName !== currentDashboard?.name) {
        try {
          await updateDashboard(id, { name: newName });
        } catch (error) {
          toast.error('Failed to update name');
        }
      }
      setIsEditing(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center gap-4 mb-8">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
        </div>
      </div>
    );
  }
  
  const gridLayout = layout.map(item => {
    let key;
    if (item.itemType === 'insightBlock') key = item.insightBlockId?.toString();
    else if (item.itemType === 'imageBlock') key = item.imageBlockId?.toString();
    else if (item.itemType === 'textBlock') key = item.textBlockId?.toString();
    else key = item.chartId?.toString() || item.i;
    return {
      i: key,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minW: 2,
      minH: 2
    };
  });
  
  const hasItems = charts.length > 0 || (insightBlocks || []).length > 0 || (imageBlocks || []).length > 0 || (textBlocks || []).length > 0;
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboards')}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>
          
          {isEditing && canEditDashboard ? (
            <input
              type="text"
              defaultValue={currentDashboard?.name}
              autoFocus
              onKeyDown={handleNameChange}
              onBlur={handleNameChange}
              className="text-2xl font-display font-bold bg-transparent border-b-2 border-primary-500 outline-none text-surface-900 dark:text-white"
            />
          ) : (
            <button
              onClick={() => canEditDashboard && setIsEditing(true)}
              className={`group flex items-center gap-2 rounded-lg px-2 py-1 -mx-2 -my-1 transition-all ${
                canEditDashboard
                  ? 'cursor-pointer hover:bg-surface-100 dark:hover:bg-surface-800'
                  : ''
              }`}
              title={canEditDashboard ? 'Click to rename dashboard' : undefined}
              disabled={!canEditDashboard}
            >
              <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {currentDashboard?.name || 'Untitled Dashboard'}
              </h1>
              {canEditDashboard && (
                <PencilSquareIcon className="w-4 h-4 text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={handleRefreshDashboard}
            disabled={isRefreshing || isLoading}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50"
            title="Refresh Dashboard"
          >
            <ArrowPathIcon className={`w-5 h-5 text-surface-600 dark:text-surface-400 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          {canShareDashboards && id && id !== 'new' && (
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
              title="Share Dashboard"
            >
              <ShareIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </button>
          )}
          {(canEditDashboard || canUseAI) && (
            <button
              onClick={() => setShowElementPicker(true)}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-surface-300 dark:border-surface-600 text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
            >
              <Squares2X2Icon className="w-4 h-4 mr-1.5 inline" />
              Add Element
            </button>
          )}
          {canCreateCharts && (
            <button onClick={() => setShowUnifiedModal(true)} className="btn-primary">
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Chart
            </button>
          )}
        </div>
      </div>
      
      {/* Dashboard grid */}
      <div id="dashboard-container" className="min-h-[600px]">
        {!hasItems ? (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
              <PlusIcon className="w-8 h-8 text-surface-400" />
            </div>
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              {canCreateCharts ? 'Add your first chart or insight' : 'No items yet'}
            </h3>
            <p className="text-surface-500 dark:text-surface-400 mb-6">
              {canCreateCharts 
                ? 'Start building your dashboard by adding charts, visualizations, or AI insight blocks'
                : 'This dashboard has no items. Contact an admin to add content.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              {canCreateCharts && (
                <button onClick={() => setShowUnifiedModal(true)} className="btn-primary">
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Chart
                </button>
              )}
              {(canEditDashboard || canUseAI) && (
                <button
                  onClick={() => setShowElementPicker(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-surface-300 dark:border-surface-600 text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                >
                  <Squares2X2Icon className="w-5 h-5 mr-2 inline" />
                  Add Element
                </button>
              )}
            </div>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 768, md: 480, sm: 0 }}
            cols={{ lg: 12, md: 6, sm: 1 }}
            rowHeight={50}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            onLayoutChange={canEditDashboard ? handleLayoutChange : undefined}
            onBreakpointChange={(newBreakpoint) => {
              currentBreakpointRef.current = newBreakpoint;
              setIsDesktop(newBreakpoint === 'lg');
            }}
            draggableHandle=".drag-handle"
            isResizable={canEditDashboard && isDesktop}
            isDraggable={canEditDashboard && isDesktop}
          >
            {layout.map(item => {
              if (item.itemType === 'insightBlock') {
                const ib = insightBlockMap[item.insightBlockId?.toString()];
                if (!ib) return null;
                return (
                  <div key={ib._id} className="card overflow-hidden h-full">
                    <InsightBlockWidget
                      insightBlock={ib}
                      refreshKey={chartRefreshKey}
                      onEdit={(block) => setEditingInsight(block)}
                      onDelete={(blockId) => {
                        removeInsightBlockLocal(blockId);
                        fetchDashboard(id);
                      }}
                      onRefresh={(updated) => {
                        if (updated) updateInsightBlockLocal(updated);
                      }}
                    />
                  </div>
                );
              }
              if (item.itemType === 'imageBlock') {
                const img = imageBlockMap[item.imageBlockId?.toString()];
                if (!img) return null;
                return (
                  <div key={img._id} className="card overflow-hidden h-full">
                    <ImageBlockWidget
                      imageBlock={img}
                      onDelete={(blockId) => {
                        removeImageBlockLocal(blockId);
                        fetchDashboard(id);
                      }}
                    />
                  </div>
                );
              }
              if (item.itemType === 'textBlock') {
                const tb = textBlockMap[item.textBlockId?.toString()];
                if (!tb) return null;
                return (
                  <div key={tb._id} className="card overflow-hidden h-full">
                    <TextBlockWidget
                      textBlock={tb}
                      onEdit={(block) => setEditingTextBlock(block)}
                      onDelete={(blockId) => {
                        removeTextBlockLocal(blockId);
                        fetchDashboard(id);
                      }}
                    />
                  </div>
                );
              }
              const chart = charts.find(c => c._id === (item.chartId?.toString() || item.chartId));
              if (!chart) return null;
              return (
                <div key={chart._id} className="card overflow-hidden h-full">
                  <ChartWidget chart={chart} refreshKey={chartRefreshKey} />
                </div>
              );
            }).filter(Boolean)}
          </ResponsiveGridLayout>
        )}
      </div>
      
      {/* Unified Add Chart Modal */}
      <UnifiedChartModal
        isOpen={showUnifiedModal}
        onClose={() => setShowUnifiedModal(false)}
        dashboardId={id}
        onChartCreated={() => {
          fetchDashboard(id);
          toast.success('Chart added to dashboard!');
        }}
      />

      {/* Add Insight Block Modal */}
      <AddInsightBlockModal
        isOpen={showInsightModal}
        onClose={() => setShowInsightModal(false)}
        dashboardId={id}
        onInsightCreated={() => {
          fetchDashboard(id);
          toast.success('Insight block added!');
        }}
      />

      {/* Edit Insight Block Modal */}
      <EditInsightBlockModal
        isOpen={!!editingInsight}
        onClose={() => setEditingInsight(null)}
        insightBlock={editingInsight}
        onUpdated={(updated) => {
          if (updated) updateInsightBlockLocal(updated);
          setEditingInsight(null);
        }}
      />

      {/* Add Image Block Modal */}
      <AddImageBlockModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        dashboardId={id}
        onImageCreated={() => {
          fetchDashboard(id);
        }}
      />

      {/* Add Text Block Modal */}
      <AddTextBlockModal
        isOpen={showTextBlockModal}
        onClose={() => setShowTextBlockModal(false)}
        dashboardId={id}
        onTextBlockCreated={() => {
          fetchDashboard(id);
        }}
      />

      {/* Edit Text Block Modal */}
      <EditTextBlockModal
        isOpen={!!editingTextBlock}
        onClose={() => setEditingTextBlock(null)}
        textBlock={editingTextBlock}
        onUpdated={(updated) => {
          if (updated) updateTextBlockLocal(updated);
          setEditingTextBlock(null);
        }}
      />

      {/* Add Element Picker Modal */}
      {showElementPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowElementPicker(false)} />
          <div className="relative bg-white dark:bg-surface-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Add Element</h2>
              <button
                onClick={() => setShowElementPicker(false)}
                className="p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-surface-500" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {canUseAI && (
                <button
                  onClick={() => { setShowElementPicker(false); setShowInsightModal(true); }}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-surface-200 dark:border-surface-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <LightBulbIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-surface-900 dark:text-white">AI Insight</p>
                    <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">AI-generated analysis</p>
                  </div>
                </button>
              )}
              {canEditDashboard && (
                <button
                  onClick={() => { setShowElementPicker(false); setShowImageModal(true); }}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-surface-200 dark:border-surface-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <PhotoIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-surface-900 dark:text-white">Image</p>
                    <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">Upload or link an image</p>
                  </div>
                </button>
              )}
              {canEditDashboard && (
                <button
                  onClick={() => { setShowElementPicker(false); setShowTextBlockModal(true); }}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-surface-200 dark:border-surface-700 hover:border-violet-400 dark:hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <DocumentTextIcon className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-surface-900 dark:text-white">Text</p>
                    <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">Rich text content</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Dashboard Modal */}
      <ShareDashboardModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        dashboardId={id}
        charts={charts}
      />
    </div>
  );
}

